<script setup lang="ts">
import { ProductName } from '@common/contracts';
import NavigationCard from '../../components/NavigationCard/NavigationCard.vue';
import ThankYouDialog from '../../components/ThankYouDialog/ThankYouDialog.vue';
import LoadingSpinner from '../../components/Loading/LoadingSpinner.vue';
import { RoutePaths } from '../../Router/types/routePaths';
import { useUserStore } from '../../stores/userStore/userStore';
import { ref } from 'vue';

const userStore = useUserStore();
const isLoading = ref(false);

const handleNavigation = () => {
  isLoading.value = true;
};

const handleBuyProduct = () => {
  // Do nothing - this prevents the loading screen from appearing
};
</script>

<template>
  <div class="dashboard">
    <ThankYouDialog />
    <LoadingSpinner v-if="isLoading" />
    <div class="dashboard__content">
      <div class="dashboard__leading">
        <img
          src="../../assets/logo.png"
          alt="Logo"
          class="dashboard__logo"
        />
        <h1 class="dashboard__title">Witamy we Wspomnieniach z Wesela!</h1>
        <p class="dashboard__subtitle">Cieszymy się, że jesteś z nami.</p>
      </div>
      <div class="dashboard__card-container">
        <NavigationCard
          :image-source="'/panstwo-mlodzi.webp'"
          title="Zdjęcia"
          :icon="'pi-images'"
          :product-name="ProductName.GOLD"
          :is-active="userStore.isVisibleGallery()"
          :next-page="RoutePaths.pictures"
          cta-title="Galeria Zdjęć & Wideo"
          cta-subtitle="Odblokuj Wasze Wspomnienia z Wesela!"
          :cta-benefits="[
            'Nieograniczone miejsce na zdjęcia i wideo',
            'Pobieranie plików w pełnej jakości – bez kompresji',
            'Prywatny link tylko dla zaproszonych gości',
          ]"
          @click="handleNavigation"
          @buy-product="handleBuyProduct"
        />
        <NavigationCard
          :image-source="'/widok-z-przodu.webp'"
          title="Lista gości"
          :icon="'pi-users'"
          :product-name="ProductName.PLATINIUM"
          :is-active="userStore.isVisibleGuestInfo()"
          :next-page="RoutePaths.guests"
          cta-title="Potwierdzenie RSVP QR"
          cta-subtitle="Zarządzaj zaproszeniami i potwierdzeniami obecności!"
          :cta-benefits="[
            'Łatwe zarządzanie listą gości',
            'Śledzenie potwierdzeń obecności',
            'Organizacja stołów i miejsc',
          ]"
          @click="handleNavigation"
          @buy-product="handleBuyProduct"
        />
        <NavigationCard
          :image-source="'/dodatki-slubne.webp'"
          title="Dodatki ślubne"
          :icon="'pi-calculator'"
          :product-name="ProductName.WEDDINGPLANER"
          :is-active="true"
          :next-page="RoutePaths.calculators"
          cta-title="Kalkulatory Ślubne"
          cta-subtitle="Zaplanuj budżet i organizację wesela!"
          :cta-benefits="['Kalkulator budżetu ślubnego', 'Planowanie harmonogramu', 'Lista zadań do wykonania']"
          @click="handleNavigation"
          @buy-product="handleBuyProduct"
        />
        <NavigationCard
          :image-source="'/materialy-do-pobrania.webp'"
          title="Materiały do pobrania"
          icon=""
          :product-name="ProductName.NIGHTGAMEGAME"
          :is-active="true"
          :next-page="RoutePaths.download"
          @click="handleNavigation"
          @buy-product="handleBuyProduct"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.dashboard {
  min-height: 100vh;
  position: relative;
  overflow: hidden;

  &__content {
    position: relative;
    z-index: 1;
    padding: 6rem 2rem;
    max-width: 1400px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 8rem;

    @media (min-width: $media-max-tablet) {
      padding: 8rem 4rem;
    }
  }

  &__leading {
    text-align: center;
    max-width: 800px;
    margin: 0 auto;
    animation: fade-in-up 0.8s ease-out;
  }

  &__logo {
    width: 300px;
    height: auto;
    margin-bottom: 2rem;
  }

  &__title {
    color: $brown-200;
    font-size: clamp(3.6rem, 4vw, 4.8rem);
    font-weight: 700;
    margin-bottom: 1.5rem;
    background: linear-gradient(135deg, $brown-200 0%, $brown-100 100%);
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-shadow: 0 2px 4px rgba($black-100, 0.1);

    @media (min-width: $media-max-tablet) {
      font-size: 4.5rem;
    }
  }

  &__subtitle {
    font-size: 2rem;
    color: $brown-200;
    font-weight: 500;
    opacity: 0.9;

    @media (min-width: $media-max-tablet) {
      font-size: 2.5rem;
    }
  }

  &__card-container {
    animation: fade-in-up 0.8s ease-out 0.2s both;
    display: flex;
    gap: 4rem;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
